import React, { useEffect, useState } from "react";
import { BemHelper } from "@/utils/BemHelper";
import classNames from "classnames";
import { Theme, ThemeFontVariant, ThemeIntent } from "../types";
import CtPublicContainer from "../public-container/CtPublicContainer";
import CtImage from "../image/CtImage";
import { THEMES, THEME_FONT_VARIANTS, THEME_INTENTS } from "../constants";
import { useActiveBreakpoints } from "@/utils/useActiveBreakpoints";
import { isLightTheme } from "../utils";

export const SECTION_INTENTS: Record<string, ThemeIntent | "dark" | "light"> = {
  DARK: "dark",
  LIGHT: "light",
  ...THEME_INTENTS,
};

export type CtSectionProps = {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  intent?: ThemeIntent | "dark" | "light";
  theme?: Theme;
  fontVariant?: ThemeFontVariant;
  featuredImageOrientation?: "left" | "right";
  featuredImage?: string;
  featuredImageAlt?: string;
  featureImageDisablePaddings?: boolean;
  featuredImageDisableBorder?: boolean;
  featuredImageSmall?: boolean;
  disablePaddings?: boolean;
  enableFeaturedContent?: boolean;
  featuredImageBorderNotCentered?: boolean;
  mobileFeaturedContentCondensed?: boolean;
};

const bem = new BemHelper("ct-section");

const MAX_FEATURED_CONTENT_EXTRA_HEIGHT = 100;

function CtSection({
  title,
  subtitle,
  children,
  theme,
  intent,
  fontVariant,
  featuredImageOrientation,
  featuredImage,
  featuredImageAlt,
  featureImageDisablePaddings,
  featuredImageDisableBorder,
  featuredImageBorderNotCentered,
  featuredImageSmall,
  enableFeaturedContent,
  disablePaddings,
  mobileFeaturedContentCondensed,
}: CtSectionProps) {
  const Container = disablePaddings ? React.Fragment : CtPublicContainer;
  const innerContainerRef = React.useRef<HTMLDivElement>(null);
  const { activeBreakpoints } = useActiveBreakpoints();
  const [imageDimensions, setImageDimensions] =
    useState<{
      widthDesktop: number;
      widthMobile: number;
    } | null>(null);

  const handleResize = () => {
    if (innerContainerRef.current) {
      setImageDimensions({
        widthDesktop: innerContainerRef.current.offsetWidth / 2,
        widthMobile: innerContainerRef.current.offsetWidth,
      });
    }
  };

  const getImageWidth = () => {
    let width = imageDimensions?.widthDesktop || 0;
    if (!activeBreakpoints.md && !mobileFeaturedContentCondensed) {
      width = imageDimensions?.widthMobile || 0;
    }

    if (featuredImageSmall) {
      width = width / 2;
    }

    return width;
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <section
      className={classNames(
        bem.block(),
        bem.modifier(theme || THEMES.THEME_1),
        bem.modifier(intent || SECTION_INTENTS.DEFAULT),
        bem.modifier(fontVariant || THEME_FONT_VARIANTS.FONT_VARIANT_1),
        subtitle && bem.modifier("has-subtitle"),
        disablePaddings && bem.modifier("disable-paddings"),
        !activeBreakpoints.md && bem.modifier("mobile"),
        isLightTheme(theme) && bem.modifier("is-light-theme"),
      )}
    >
      <Container>
        <div
          ref={innerContainerRef}
          className={classNames(
            bem.element("inner-container"),
            !activeBreakpoints.md && bem.modifier("mobile", "inner-container"),
            featuredImage && bem.modifier(featuredImageOrientation || "left", "inner-container"),
            featuredImage && bem.modifier("has-featured-image", "inner-container"),
            featuredImageSmall && bem.modifier("featured-image-small", "inner-container"),
            enableFeaturedContent && bem.modifier("has-featured-content", "inner-container"),
            mobileFeaturedContentCondensed &&
              bem.modifier("mobile-featured-content-condensed", "inner-container"),
          )}
        >
          {title && !activeBreakpoints.md && !mobileFeaturedContentCondensed && (
            <h2 className={classNames(bem.element("title"), bem.modifier("mobile", "title"))}>
              {title}
            </h2>
          )}
          {featuredImage && (
            <CtImage
              className={classNames(
                bem.element("featured-image"),
                !activeBreakpoints.md && bem.modifier("mobile", "featured-image"),
              )}
              src={featuredImage}
              alt={featuredImageAlt || title}
              theme={theme}
              square
              disablePaddings={featureImageDisablePaddings}
              disableBorder={featuredImageDisableBorder}
              borderNotCentered={featuredImageBorderNotCentered}
              width={getImageWidth()}
            />
          )}
          <div
            className={classNames(
              bem.element("content-container"),
              enableFeaturedContent && bem.modifier("has-featured-content", "content-container"),
              !activeBreakpoints.md && bem.modifier("mobile", "content-container"),
              mobileFeaturedContentCondensed &&
                bem.modifier("mobile-featured-content-condensed", "content-container"),
            )}
            style={
              imageDimensions && enableFeaturedContent
                ? {
                    maxHeight:
                      (!activeBreakpoints.md
                        ? imageDimensions.widthMobile
                        : imageDimensions.widthDesktop) + MAX_FEATURED_CONTENT_EXTRA_HEIGHT,
                    overflowY: "auto",
                  }
                : undefined
            }
          >
            {title && (activeBreakpoints.md || mobileFeaturedContentCondensed) && (
              <h2
                className={classNames(
                  bem.element("title"),
                  featuredImage && bem.modifier("has-featured-image", "title"),
                  mobileFeaturedContentCondensed && bem.modifier("mobile", "title"),
                )}
              >
                {title}
              </h2>
            )}
            {subtitle && <p className={classNames(bem.element("subtitle"))}>{subtitle}</p>}
            <div className={classNames(bem.element("content"))}>{children}</div>
          </div>
        </div>
      </Container>
    </section>
  );
}

CtSection.defaultProps = {
  title: null,
  intent: SECTION_INTENTS.DEFAULT,
  theme: THEMES.THEME_1,
  fontVariant: THEME_FONT_VARIANTS.FONT_VARIANT_1,
  subtitle: null,
  featuredImageOrientation: "left",
  featuredImage: null,
  featuredImageAlt: null,
  featureImageDisablePaddings: false,
  featuredImageDisableBorder: false,
  featuredImageBorderNotCentered: false,
  featuredImageSmall: false,
  disablePaddings: false,
  enableFeaturedContent: false,
  mobileFeaturedContentCondensed: false,
};

export default CtSection;
